import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Construction, RefreshCw, ExternalLink, AlertTriangle, MapPin } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoadworksSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);

  const fetchRoadworks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/roadworks`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching roadworks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoadworks();
  }, []);

  const getStatusColor = (status) => {
    if (status === 'Active') return '#ff6b6b';
    if (status === 'Planned') return '#ffa94d';
    return '#69db7c';
  };

  const getReasonIcon = (reason) => {
    if (reason.includes('Construction')) return '🏗️';
    if (reason.includes('Water')) return '💧';
    if (reason.includes('Gas')) return '🔥';
    if (reason.includes('Electrical')) return '⚡';
    if (reason.includes('Telecoms')) return '📡';
    if (reason.includes('event')) return '🎉';
    return '🚧';
  };

  return (
    <div className="content-card content-card-large" data-testid="roadworks-section">
      <div className="card-header">
        <h2>
          <Construction size={20} className="inline" style={{ color: '#ffa94d' }} /> Road Works & Closures
        </h2>
        <Button onClick={fetchRoadworks} variant="outline" size="sm" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
        </Button>
      </div>

      <ScrollArea className="card-content">
        {loading ? (
          <p className="loading-text">Loading roadworks...</p>
        ) : data?.error ? (
          <p className="error-text">{data.error}</p>
        ) : (
          <div>
            {data?.roadworks?.length > 0 ? (
              <div className="roadworks-list">
                {data.roadworks.map((item, index) => (
                  <div
                    key={`${item.road}-${item.dates}-${index}`}
                    className="roadwork-item"
                    style={{
                      padding: '0.75rem 1rem',
                      marginBottom: '0.75rem',
                      background: index % 2 === 0 ? 'rgba(255,169,77,0.1)' : 'rgba(255,255,255,0.05)',
                      borderRadius: '8px',
                      borderLeft: `4px solid ${getStatusColor(item.status)}`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                          {getReasonIcon(item.reason)} {item.road}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '0.25rem' }}>
                          <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          {item.location}
                        </div>
                        <div style={{ fontSize: '0.85rem' }}>
                          <strong>Reason:</strong> {item.reason}
                        </div>
                        <div style={{ fontSize: '0.85rem' }}>
                          <strong>Dates:</strong> {item.dates}
                        </div>
                      </div>
                      <span
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: getStatusColor(item.status),
                          color: '#000'
                        }}
                      >
                        {item.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                <Construction size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>No roadworks reported</p>
              </div>
            )}

            {data?.total_count > 0 && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                background: 'rgba(255,255,255,0.05)', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '0.85rem', color: '#aaa', marginBottom: '0.5rem' }}>
                  Showing {data.roadworks.length} of {data.total_count} roadworks
                </p>
                <p style={{ fontSize: '0.8rem', color: '#666' }}>
                  Last updated: {data.last_updated}
                </p>
              </div>
            )}

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <Button
                onClick={() => setShowMap(!showMap)}
                variant="outline"
                size="sm"
              >
                {showMap ? 'Hide Map' : 'Show Live Map'}
              </Button>
            </div>

            {showMap && (
              <div style={{ marginTop: '1rem', borderRadius: '8px', overflow: 'hidden', border: '2px solid rgba(255,169,77,0.3)' }}>
                <iframe
                  src="https://portal.one.network/prd-portal-one-network/embed/?options=%7B%22googleAPIKey%22%3A%22AIzaSyAIZ7DGwyv_tr9qk-qoctzwcYLrT4FQZto%22%2C%22organisationID%22%3A212%2C%22embedded%22%3Atrue%2C%22data%22%3A%7B%22layersActive%22%3A%5B%22INCIDENTS_LIVE_INCIDENT%22%2C%22INCIDENTS_LIVE_ACCIDENT%22%2C%22INCIDENTS_LIVE_ROADCLOSURE%22%2C%22INCIDENTS_LIVE_LANECLOSURE%22%2C%22TM_LAYER_ROADCLOSURE_LIVE%22%2C%22TM_LAYER_DIVERSIONROUTE_LIVE%22%2C%22TM_LAYER_LANECLOSURE_LIVE%22%2C%22TM_LAYER_TWOWAYSIGNALS_LIVE%22%2C%22ROADWORKS_CURRENT%22%5D%7D%2C%22embedID%22%3A%22LZGUD1OI56%22%2C%22system%22%3A%7B%22delayedLoad%22%3Afalse%2C%22historicWorksEnabled%22%3Afalse%7D%2C%22map%22%3A%7B%22swLat%22%3A52.55%2C%22swLng%22%3A0.05%2C%22neLat%22%3A52.95%2C%22neLng%22%3A0.75%7D%7D"
                  width="100%"
                  height="350"
                  style={{ border: 'none' }}
                  title="West Norfolk Roadworks Map"
                  loading="lazy"
                />
                <p style={{ fontSize: '0.75rem', color: '#888', textAlign: 'center', padding: '0.5rem', background: 'rgba(0,0,0,0.3)' }}>
                  Live map powered by one.network - click markers for details
                </p>
              </div>
            )}

            <div style={{ 
              marginTop: '1rem', 
              padding: '0.5rem', 
              background: 'rgba(255,152,0,0.1)', 
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.8rem',
              color: '#ffa94d'
            }}>
              <AlertTriangle size={14} />
              <span>Covers A47, A17, A10, A148, A149 + all local roads via map</span>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default RoadworksSection;
